import { Controller } from "@hotwired/stimulus"
import tippy from "tippy.js"

// Renders an expandable list of inputs whose trimmed values are joined by
// `delimiterValue` and written to a single hidden form field. The visible
// inputs themselves are unnamed — they're scratch space; only the hidden
// field is submitted.
//
// Known limitation: a value that contains the delimiter cannot round-trip
// through this control. Callers should pick a delimiter that won't appear
// in their values (e.g. comma works fine for API keys).
export default class extends Controller {
  static targets = ["container", "template", "hiddenField", "input", "removeButton", "label"]
  static values = {
    delimiter: { type: String, default: "," },
    minItems: { type: Number, default: 1 },
  }

  connect() {
    this.assignRowIds()
    this.initRemoveButtonTooltips()
    this.sync()
    this.enforceMinItems()
  }

  add(event) {
    event.preventDefault()

    const fragment = this.templateTarget.content.cloneNode(true)
    this.containerTarget.appendChild(fragment)

    this.assignRowIds()
    this.initRemoveButtonTooltips()
    this.sync()
    this.enforceMinItems()

    const newInput = this.inputTargets[this.inputTargets.length - 1]
    if (newInput) {
      newInput.focus()
    }
  }

  remove(event) {
    event.preventDefault()

    if (this.inputTargets.length <= this.minItemsValue) return

    const button = event.currentTarget
    const row = button.closest("[data-multi-text-input-row]")
    if (!row) return

    // Only the first row carries HTML5 `required` (see the component's
    // render_row comment). If we're removing that row, promote the
    // requirement to whichever row is about to become first, so users can't
    // strip the browser's "at least one value" gate by deleting the first
    // input. Rows added via `add()` come from the <template> and never have
    // `required`, so we don't need any equivalent handling there.
    const removedInput = row.querySelector("[data-multi-text-input-target='input']")
    const wasRequired = removedInput?.required === true

    row.remove()

    if (wasRequired && this.inputTargets[0]) {
      this.inputTargets[0].required = true
    }

    this.sync()
    this.enforceMinItems()
  }

  sync() {
    const joined = this.inputTargets
      .map((input) => input.value.trim())
      .filter((value) => value.length > 0)
      .join(this.delimiterValue)

    this.hiddenFieldTarget.value = joined
  }

  assignRowIds() {
    this.inputTargets.forEach((input) => {
      if (!input.id) {
        input.id = this._nextRowId()
      }
      const row = input.closest("[data-multi-text-input-row]")
      const label = row?.querySelector("[data-multi-text-input-target='label']")
      if (label && label.htmlFor !== input.id) {
        label.htmlFor = input.id
      }
    })
  }

  initRemoveButtonTooltips() {
    if (this.hasRemoveButtonTarget) {
      tippy(this.removeButtonTargets)
    }
  }

  _nextRowId() {
    if (!this._idBase) {
      this._idBase = `mti-${Math.random().toString(36).slice(2, 9)}`
      this._idCounter = 0
    }
    this._idCounter += 1
    return `${this._idBase}-${this._idCounter}`
  }

  enforceMinItems() {
    const atFloor = this.inputTargets.length <= this.minItemsValue

    if (this.hasRemoveButtonTarget) {
      this.removeButtonTargets.forEach((button) => {
        button.disabled = atFloor
        button.hidden = atFloor
        button.classList.toggle("hidden", atFloor)
      })
    }
  }
}
